import React, { useMemo } from 'react';
import { ManagedPopup } from 'js/components/common/Popup';
import { useFormatter } from 'js/hooks/useFormatter';
import { Button } from 'js/components/common/Button';

import * as hooks from './hooks';

import style from './TicketDetailsPopup.scss';

export const popupClassNames = {
    inner: style.popupInner
};

export const TicketDetailsPopup = ({ data: { transaction, t } }) => {
    const formatter = useFormatter();
    const title = t('popup.title');
    const betDate = formatter.getDateTimeLong(formatter.parseImsDate(transaction.gameSessionStart));
    const operator = t('popup.operator.value', null);
    const address = t('popup.address.value', null);
    const city = t('popup.city.value', null);
    const { sportsbookGamingDetails } = transaction;
    const {
        ticketOdds,
        potentialPayout,
        selections
    } = sportsbookGamingDetails;

    const rows = useMemo(() => {
        const uniqueEventNames = [...new Set(selections.map((selection) => selection.eventName + selection.eventStartTime))];

        return [
            [t('popup.ticketNumber'), transaction.gameSessionCode],
            [t('popup.betDate'), betDate],
            [t('popup.operator'), operator],
            [t('popup.address'), address],
            [t('popup.city'), city],
            [t('popup.playerName'), transaction.playerName],
            [t('popup.bet'), transaction.bet && formatter.formatCurrency(transaction.bet)],
            [t('popup.win'), transaction.win && formatter.formatCurrency(transaction.win)],
            [t('popup.ticketOdds'), ticketOdds],
            [t('popup.potentialPayout'), formatter.formatCurrency(potentialPayout)],
            [t('popup.type'), uniqueEventNames.length > 1 ? t('popup.type.multiple') : t('popup.type.single')],
            [t('popup.gameName'), transaction.gameName]
        ];
    }, [
        transaction,
        betDate,
        operator,
        address,
        city,
        ticketOdds,
        potentialPayout,
        selections,
        formatter,
        t
    ]);

    const data = useMemo(() => {
        return [
            [
                t('popup.eventName'),
                t('popup.eventStartTime'),
                t('popup.leagueName'),
                t('popup.expectedResult')
            ],
            ...selections.map((selection) => [
                [selection.eventName, selection.sportName],
                formatter.getDateTimeLong(formatter.parseImsDate(selection.eventStartTime)),
                selection.leagueName,
                selection.expectedResult
            ])
        ];
    }, [
        t,
        formatter,
        selections
    ]);

    const ticketDownload = hooks.useTicketDownload({ t, rows, data });

    return (
        <ManagedPopup title={title} classNames={popupClassNames}>
            {
                rows.map(([key, value], index) => value && (
                    <div className={style.row} key={index}>
                        <div>{key}</div>
                        <div>{value}</div>
                    </div>
                ))
            }
            <div className={style.selections}>
                {
                    data.map((values, index) => (
                            <div key={index} className={style.selection}>
                                {
                                    values.map((value, subIndex) => (
                                        <div key={`${index}-${subIndex}`} className={style.cell}>{
                                            Array.isArray(value) ?
                                                value.map((line, lineIndex) => (
                                                    <div key={lineIndex}>{line}</div>
                                                )) :
                                                value
                                        }</div>
                                    ))
                                }
                            </div>
                        )
                    )
                }
            </div>
            <div className={style.footer}>
                <div className={style.text}>{t('footer.description', null)}</div>
                <div className={style.download}>
                    <Button
                        onClick={ticketDownload}
                    >
                        {t('footer.download')}
                    </Button>
                </div>
            </div>
        </ManagedPopup>
    );
};
